import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  PLAN_STATUS,
  getPlanTargetsSummary,
  resolvePlanStatusForPlan,
  planMatchesStatusFilter,
  hasMealPlan,
} from "../../../../utils/nutrition-progress";
import SvgIcon from "../../../../parts/SvgIcon";

const toUtcNum = (date) => Number(moment(date).format("YYYYMMDD"));

const UTENSILS_ALT_ICON = "/img/widget-icons/utensils-alt.svg";
const ARROW_RIGHT_ICON = "/img/new-icons/arrow-forward.svg";
const CHECK_ICON = "/img/svg/check.svg";
const EDIT_GRADIENT_ICON = "/img/new-icons/edit-gradient-icon.svg";

const PlanStatusBar = ({ planStatus, hasMeal, summary, planId, showCreateNewPlan }) => {
  const { t } = useTranslation("athlete-portal");
  const psb = (key, opts) => t(`nutritionPage.progress.planStatusBar.${key}`, opts);

  if (planStatus === PLAN_STATUS.NONE || summary === null) {
    return (
      <div className="np-plan-bar np-plan-bar--none">
        <div className="np-plan-bar__info">
          <span className="np-plan-bar__icon"><SvgIcon src={UTENSILS_ALT_ICON} useCurrentColor /></span>
          <div>
            <div className="np-plan-bar__title">{psb("noActivePlan")}</div>
            <div className="np-plan-bar__subtitle">{psb("noTargetsSet")}</div>
          </div>
        </div>
        <a // NOSONAR
          className="np-plan-bar__cta"
          role="button"
          data-button-type="primary"
          data-icon
          href="/actions/refresh?modal[type]=create-nutrition-plan"
        >
          <img src="/img/new-icons/add.svg" alt="" />{psb("createPlan")}
        </a>
      </div>
    );
  }

  const isDue    = planStatus === PLAN_STATUS.DUE;
  const isActive = planStatus === PLAN_STATUS.ACTIVE || planStatus === PLAN_STATUS.ACTIVE_MEAL;
  // Same signal as the "Create New Plan" CTA: only the one due card that
  // actually gets the CTA is worth calling out in amber — older due plans
  // further down the (already-sorted-newest-first) list are just history.
  const dueClass = showCreateNewPlan ? "np-plan-bar--due" : "np-plan-bar--due-muted";
  // "due for a new plan" only makes sense on the one card that's actually
  // actionable (showCreateNewPlan — same signal dueClass above already uses).
  // An older due plan further down the list just means a newer plan (possibly
  // active) came after it — still worth noting it ended, but not implying
  // the client is currently without a plan.
  const endedKey = showCreateNewPlan ? "endedDue" : "ended";
  const subtitle = isDue
    ? psb(endedKey, { date: summary.endDate })
    : psb("active");

  return (
    <div className={`np-plan-bar ${isActive ? "np-plan-bar--active" : dueClass}`}>
      <div className="np-plan-bar__info">
        <span className="np-plan-bar__icon"><SvgIcon src={UTENSILS_ALT_ICON} useCurrentColor /></span>
        <div>
          <div className="np-plan-bar__title">{psb("nutritionPlanLabel", { label: summary.label })}</div>
          <div className="np-plan-bar__subtitle">{subtitle}</div>
        </div>
      </div>

      <div className="np-plan-bar__targets">
        <div className="np-plan-bar__day-row">
          <span className="np-plan-bar__day-label">{psb("trainingDay")}</span>
          <span className="np-plan-bar__kcal">
            {summary.trainingKcal == null ? "—" : summary.trainingKcal.toLocaleString()} kCal
          </span>
        </div>
        {summary.restKcal != null && (
          <div className="np-plan-bar__day-row">
            <span className="np-plan-bar__day-label">{psb("restDay")}</span>
            <span className="np-plan-bar__kcal">{summary.restKcal.toLocaleString()} kCal</span>
          </div>
        )}
      </div>

      {hasMeal ? (
        <div className="np-plan-bar__chip np-plan-bar__chip--has-meal">
          <SvgIcon src={CHECK_ICON} useCurrentColor classNames="np-plan-bar__chip-icon" />
          {psb("mealPlanAssigned")}
        </div>
      ) : (
        // A plan that already ended has nothing left to build a meal plan
        // for — no point offering the CTA once it's DUE.
        !isDue && (
          <a // NOSONAR
            className="np-plan-bar__chip np-plan-bar__chip--no-meal"
            role="button"
            href={`/actions/refresh?modal[type]=create-meal-plan&plan=${planId}`}
          >
            <SvgIcon src={UTENSILS_ALT_ICON} useCurrentColor classNames="np-plan-bar__chip-icon" />
            {psb("noMealPlanBuild")}
            <SvgIcon src={ARROW_RIGHT_ICON} useCurrentColor classNames="np-plan-bar__chip-arrow" />
          </a>
        )
      )}

      <div className="np-plan-bar__actions">
        {isDue ? (
          // Only the most recent plan's card offers this CTA — with several
          // due plans in range (a long historical period), repeating "Create
          // New Plan" on every one of them is redundant; only the latest due
          // plan is actually the one missing a successor.
          showCreateNewPlan && (
            <a // NOSONAR
              className="np-plan-bar__cta"
              role="button"
              data-button-type="primary"
              data-icon
              href="/actions/refresh?modal[type]=create-nutrition-plan"
            >
              <img src="/img/new-icons/add.svg" alt="" />{psb("createNewPlan")}
            </a>
          )
        ) : (
          <a // NOSONAR
            className="np-plan-bar__cta"
            role="button"
            data-button-type="secondary"
            href={`/actions/refresh?modal[type]=create-nutrition-plan&plan=${planId}`}
          >
            <SvgIcon src={EDIT_GRADIENT_ICON} classNames="np-plan-bar__cta-icon" />
            <span className="np-plan-bar__cta-label">{psb("editPlan")}</span>
          </a>
        )}
      </div>
    </div>
  );
};

PlanStatusBar.propTypes = {
  planStatus: PropTypes.oneOf(Object.values(PLAN_STATUS)),
  hasMeal: PropTypes.bool,
  planId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showCreateNewPlan: PropTypes.bool,
  summary: PropTypes.shape({
    label: PropTypes.string,
    endDate: PropTypes.string,
    trainingKcal: PropTypes.number,
    restKcal: PropTypes.number,
    protein: PropTypes.number,
  }),
};

const PlanStatusList = ({ nutritionPlans, dateFrom, dateTo, planStatusFilter, todayUtc }) => {
  const plansInRange = nutritionPlans
    .filter((plan) => plan.utcEndDate >= toUtcNum(dateFrom) && plan.utcStartDate <= toUtcNum(dateTo))
    .filter((plan) => planMatchesStatusFilter(plan, planStatusFilter, todayUtc))
    .sort((a, b) => b.utcStartDate - a.utcStartDate);

  if (plansInRange.length === 0) {
    return (
      <div className="mb-3">
        <PlanStatusBar
          planStatus={PLAN_STATUS.NONE}
          hasMeal={false}
          summary={null}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {plansInRange.map((plan, index) => (
        <PlanStatusBar
          key={String(plan._id)}
          planId={plan._id}
          planStatus={resolvePlanStatusForPlan(plan, todayUtc)}
          hasMeal={hasMealPlan(plan)}
          summary={getPlanTargetsSummary(plan)}
          showCreateNewPlan={index === 0}
        />
      ))}
    </div>
  );
};

PlanStatusList.propTypes = {
  nutritionPlans: PropTypes.arrayOf(PropTypes.object),
  dateFrom: PropTypes.instanceOf(Date),
  dateTo: PropTypes.instanceOf(Date),
  planStatusFilter: PropTypes.oneOf(Object.values(PLAN_STATUS)),
  todayUtc: PropTypes.number,
};

export default PlanStatusList;
